import { useDataProvider, useInvalidate, useOne, HttpError } from "@refinedev/core";
import Button from "components/button";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TEMPLATE_PATH } from "./template";
import { ImportStatusModal } from "./statusModal";

interface ImportResult {
  status: "processing" | "success" | "error";
  task_id?: string;
  message?: string | null;
  file_link?: string;
}

interface ImportResponse {
  result: boolean;
  message?: string;
  data?: ImportResult;
}

interface ImportPickupAccountProps {
  onBusyChange: (busy: boolean) => void;
}

const POLL_INTERVAL = 2000;
const supportedFile = /\.(xlsx|xls|csv)$/i;

const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

const isImportResult = (data?: ImportResult): data is ImportResult =>
  !!data && ["processing", "success", "error"].includes(data.status);

const downloadUrl = (link?: string) => {
  if (!link) return "";
  try {
    const url = new URL(link);
    return ["https:", "http:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
};

export const ImportPickupAccount = ({ onBusyChange }: ImportPickupAccountProps) => {
  const navigate = useNavigate();
  const getDataProvider = useDataProvider();
  const provider = getDataProvider();
  const invalidate = useInvalidate();
  const mounted = useRef(false);
  const submitting = useRef(false);
  const [eventId, setEventId] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [isEditable, setIsEditable] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState("");
  const [pollError, setPollError] = useState("");
  const [retry, setRetry] = useState(0);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const locked = uploading || !!taskId;

  const { data: events, isFetching: fetchingEvents } = useOne<{ id: string; name: string }[], HttpError>({
    resource: "pickup/events/dropdown",
    id: "",
    meta: { resource: "data" },
  });
  const eventOptions = (events?.data ?? []).map((event) => ({ value: event.id, label: event.name }));
  const { data: categories, isFetching: fetchingCategories, isError: categoriesFailed, refetch: reloadCategories } = useOne<string[], HttpError>({
    resource: "pickup/events",
    id: `${eventId}/categories/dropdown`,
    meta: { resource: "data" },
    queryOptions: { enabled: !!eventId, keepPreviousData: false },
  });
  const categoryOptions = Array.from(new Set(categories?.data ?? [])).map((category) => ({ value: category, label: category }));
  const ready = !!eventId && selectedCategories.length > 0 && !categoriesFailed
    && selectedCategories.every((category) => categoryOptions.some((option) => option.value === category));

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    onBusyChange(locked);
    return () => onBusyChange(false);
  }, [locked, onBusyChange]);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const response = await provider.getOne<ImportResult>({
          resource: "pickup/accounts/import/result",
          id: taskId,
          meta: { resource: "data" },
        });
        if (cancelled) return;
        if (!isImportResult(response.data)) throw new Error("Invalid import result. Please check again.");

        setResult(response.data);
        if (response.data.status === "processing") {
          // Schedule only after the current request completes, avoiding overlapping polls.
          timer = setTimeout(poll, POLL_INTERVAL);
        } else {
          setTaskId(null);
          void invalidate({ resource: "pickup/accounts", invalidates: ["list"] });
        }
      } catch (error) {
        if (!cancelled) {
          setPollError(errorMessage(error, "Unable to check import status. Please check again."));
        }
      }
    };

    setPollError("");
    timer = setTimeout(poll, POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [taskId, retry, provider, invalidate]);

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    setResult(null);
    setFile(null);
    setError("");
    if (!selected) return;
    if (!supportedFile.test(selected.name)) {
      setError("Please select an .xlsx, .xls or .csv file.");
      event.target.value = "";
      return;
    }
    if (!selected.size) {
      setError("The selected file is empty.");
      event.target.value = "";
      return;
    }
    setFile(selected);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (locked || submitting.current) return;
    if (!ready || !file) {
      setError("Please select an event, at least one category and a file to import.");
      return;
    }
    submitting.current = true;
    setStatusModalOpen(true);
    setUploading(true);
    setResult(null);
    setError("");
    setPollError("");
    const form = new FormData();
    form.append("event_id", eventId);
    form.append("category", selectedCategories.join(", "));
    form.append("file", file);
    form.append("is_active", String(isActive));
    form.append("is_editable", String(isEditable));
    try {
      const response = await provider.create<ImportResponse, FormData>({
        resource: "pickup/accounts/import",
        variables: form,
        meta: { ignoreResourceWrapper: true },
      });
      if (!mounted.current) return;
      const payload = response.data;
      if (!payload.result || !isImportResult(payload.data)) {
        throw new Error(payload.message || "Unable to start import. No valid task was returned.");
      }
      if (payload.data.status === "processing") {
        if (!payload.data.task_id) throw new Error("Unable to track import: no task ID was returned.");
        setTaskId(payload.data.task_id);
      } else {
        void invalidate({ resource: "pickup/accounts", invalidates: ["list"] });
      }
      setResult(payload.data);
    } catch (error) {
      if (mounted.current) setError(errorMessage(error, "Unable to upload the file."));
    } finally {
      submitting.current = false;
      if (mounted.current) setUploading(false);
    }
  };

  const fileLink = downloadUrl(result?.file_link);

  return (
    <form onSubmit={submit} className="w-full min-w-0 flex flex-col gap-4">
      <div className="box-container">
        <p className="mb-4 text-sm text-gray-600">Select an event and categories to import pickup accounts. The selected categories, status and update permission apply to the imported accounts.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="pickup-import-event" className="block text-sm font-medium mb-2">Event name <span className="text-red-500">*</span></label>
            <Select
              inputId="pickup-import-event"
              options={eventOptions}
              value={eventOptions.find((option) => option.value === eventId) ?? null}
              placeholder="Select event"
              isLoading={fetchingEvents}
              isDisabled={locked}
              onChange={(option: { value: string } | null) => {
                const nextEventId = option?.value ?? "";
                if (nextEventId === eventId) return;
                setEventId(nextEventId);
                setSelectedCategories([]);
                setFile(null);
                setResult(null);
                setError("");
                setPollError("");
              }}
            />
          </div>
          <div>
            <label htmlFor="pickup-import-category" className="block text-sm font-medium mb-2">Category <span className="text-red-500">*</span></label>
            <Select
              key={eventId}
              inputId="pickup-import-category"
              isMulti
              options={categoryOptions}
              value={categoryOptions.filter((option) => selectedCategories.includes(option.value))}
              placeholder="Select categories"
              isLoading={fetchingCategories}
              isDisabled={!eventId || locked || fetchingCategories || categoriesFailed}
              onChange={(options: readonly { value: string }[]) => {
                setSelectedCategories(options.map((option) => option.value));
                setFile(null);
                setResult(null);
                setError("");
                setPollError("");
              }}
            />
            {eventId && categoriesFailed && <div role="alert" className="mt-2">
              <p className="error-text">Unable to load categories. Please try again.</p>
              <Button className="mt-2" disabled={fetchingCategories} onClick={() => reloadCategories()}>Reload categories</Button>
            </div>}
            {eventId && !fetchingCategories && !categoriesFailed && categories && categoryOptions.length === 0 && <p className="mt-2 text-sm text-gray-500">This event has no categories available for import.</p>}
          </div>
          {ready && <>
          <div>
            <label htmlFor="pickup-import-file" className="block text-sm font-medium mb-2">Excel / CSV file <span className="text-red-500">*</span></label>
            <input key={JSON.stringify([eventId, selectedCategories])} id="pickup-import-file" type="file" accept=".xlsx,.xls,.csv" disabled={locked} onChange={selectFile} className="block w-full text-sm" />
            <p className="mt-2 text-xs text-gray-500">Supported formats: .xlsx, .xls, .csv.</p>
            <a
              href={`${process.env.PUBLIC_URL || ""}${TEMPLATE_PATH}`}
              download
              aria-disabled={locked}
              className={`button mt-3 inline-flex bg-white text-brand-500 border border-brand-500 hover:bg-gray-50 active:bg-gray-100 ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={(event) => { if (locked) event.preventDefault(); }}
            >Download Excel template</a>
            <p className="mt-2 text-xs text-gray-500">The template contains Role name, Username, Password, Account name and Pickup Type. Categories are selected above. For admin accounts, leave Account name and Pickup Type blank.</p>
          </div>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="mb-2 text-sm font-medium">Status</p>
              <SwitchButton label="Active" checked={isActive} disabled={locked} onChange={setIsActive} />
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">Update permission</p>
              <SwitchButton label="Allow updates" checked={isEditable} disabled={locked} onChange={setIsEditable} />
            </div>
          </div>
          </>}
        </div>
      </div>

      {!statusModalOpen && error && (
        <div className="box-container" aria-live="polite">
          <p role="alert" className="error-text">{error}</p>
        </div>
      )}

      <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
        <div className="flex items-center justify-end gap-x-4">
          <Button
            disabled={locked}
            onClick={() => navigate("/pickup/accounts")}
            className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
          >Cancel</Button>
          {ready && <Button type="submit" disabled={locked || !file || result?.status === "success" || result?.status === "error"} className="min-w-[80px] whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">{uploading ? "Uploading…" : taskId ? "Processing…" : "Import accounts"}</Button>}
        </div>
      </div>
      <ImportStatusModal
        open={statusModalOpen}
        uploading={uploading}
        processing={!!taskId}
        error={error}
        pollError={pollError}
        result={result}
        fileLink={fileLink}
        onCheckAgain={() => setRetry((value) => value + 1)}
        onClose={() => {
          if (!uploading && !taskId) setStatusModalOpen(false);
        }}
      />
    </form>
  );
};
